import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const EntitlementPolicySchema = z.enum(["IMMEDIATE", "END_OF_TERM"]);
const BillingPolicySchema = z.enum(["IMMEDIATE", "END_OF_TERM", "START_OF_TERM"]);

const ScheduledEventSchema = z.object({
  eventId: z.string(),
  eventType: z.string(),
  effectiveDate: z.string(),
  plan: z.string().nullable(),
  phase: z.string().nullable(),
});

const SubscriptionLineSchema = z.object({
  subscriptionId: z.string(),
  planName: z.string(),
  planDisplayName: z.string(),
  productCategory: z.enum(["BASE", "ADD_ON", "STANDALONE"]),
  phaseType: z.enum(["TRIAL", "EVERGREEN", "DISCOUNT", "FIXEDTERM"]),
  state: z.enum(["PENDING", "ACTIVE", "CANCELLING", "BLOCKED", "CANCELLED"]),
  billingPeriod: z.string(),
  startDate: z.string().nullable(),
  chargedThroughDate: z.string().nullable(),
  cancelledDate: z.string().nullable(),
  billingEndDate: z.string().nullable(),
  scheduledEvents: z.array(ScheduledEventSchema),
});

const SubscriptionViewSchema = z.object({
  bundleId: z.string(),
  franchiseId: z.string(),
  externalKey: z.string().nullable(),
  subscriptions: z.array(SubscriptionLineSchema),
});

const CreateSubscriptionInputSchema = z.object({
  franchiseId: z.string(),
  planName: z.string(),
  addOnPlanNames: z.array(z.string()).optional(),
  /** When the subscription's entitlement (feature access) starts. Past dates allowed for migration. */
  entitlementDate: z.string().optional(),
  /** When KB starts billing. Set in the future to honor an externally-paid period. */
  billingDate: z.string().optional(),
  /** Marks subscription as migrated from a prior system. Audit flag only. */
  migrated: z.boolean().optional(),
});

const EditSubscriptionInputSchema = z.object({
  basePlanName: z.string(),
  addOnPlanNames: z.array(z.string()),
  billingPolicy: BillingPolicySchema.optional(),
  requestedDate: z.string().optional(),
});

const AddAddOnsInputSchema = z.object({
  addOnPlanNames: z.array(z.string()).min(1),
  entitlementDate: z.string().optional(),
  billingDate: z.string().optional(),
});

const CancelSubscriptionInputSchema = z.object({
  entitlementPolicy: EntitlementPolicySchema.default("END_OF_TERM"),
  billingPolicy: BillingPolicySchema.default("END_OF_TERM"),
  requestedDate: z.string().optional(),
});

const CancelAddOnInputSchema = CancelSubscriptionInputSchema;

const PreviewChargeSchema = z.object({
  description: z.string(),
  planName: z.string().nullable(),
  phase: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
});

const PreviewResponseSchema = z.object({
  charges: z.array(PreviewChargeSchema),
  totalAmount: z.number(),
  currency: z.string(),
  nextBillingDate: z.string().nullable(),
});

const errorSchema = z.object({ message: z.string() });

export const subscriptionContract = c.router({
  getSubscription: {
    method: "GET",
    path: "/billing/subscriptions/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    responses: {
      200: SubscriptionViewSchema.nullable(),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get the franchise's single active subscription, or null if none",
  },

  createSubscription: {
    method: "POST",
    path: "/billing/subscriptions",
    body: CreateSubscriptionInputSchema,
    responses: {
      201: SubscriptionViewSchema,
      400: errorSchema,
      401: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Create the franchise's subscription (base plan + optional add-ons)",
  },

  addAddOns: {
    method: "POST",
    path: "/billing/subscriptions/:franchiseId/addons",
    pathParams: z.object({ franchiseId: z.string() }),
    body: AddAddOnsInputSchema,
    responses: {
      201: SubscriptionViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Attach one or more add-ons to the franchise's subscription",
  },

  editSubscription: {
    method: "PATCH",
    path: "/billing/subscriptions/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    body: EditSubscriptionInputSchema,
    responses: {
      200: SubscriptionViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Diff-based edit of the franchise's subscription",
  },

  previewEdit: {
    method: "POST",
    path: "/billing/subscriptions/:franchiseId/preview",
    pathParams: z.object({ franchiseId: z.string() }),
    body: EditSubscriptionInputSchema,
    responses: {
      200: PreviewResponseSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Dry-run preview of projected charges for a subscription edit",
  },

  cancelSubscription: {
    method: "DELETE",
    path: "/billing/subscriptions/:franchiseId",
    pathParams: z.object({ franchiseId: z.string() }),
    body: CancelSubscriptionInputSchema,
    responses: {
      200: z.object({ message: z.string() }),
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Cancel the franchise's subscription",
  },

  reactivateSubscription: {
    method: "POST",
    path: "/billing/subscriptions/:franchiseId/reactivate",
    pathParams: z.object({ franchiseId: z.string() }),
    body: z.object({}),
    responses: {
      200: SubscriptionViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Undo a scheduled cancellation on the BASE subscription",
  },

  cancelAddOn: {
    method: "DELETE",
    path: "/billing/subscriptions/:franchiseId/addons/:subscriptionId",
    pathParams: z.object({ franchiseId: z.string(), subscriptionId: z.string() }),
    body: CancelAddOnInputSchema,
    responses: {
      200: z.object({ message: z.string() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Cancel a single add-on on the franchise's subscription",
  },

  deleteScheduledChange: {
    method: "DELETE",
    path: "/billing/subscriptions/:franchiseId/scheduled/:subscriptionId",
    pathParams: z.object({
      franchiseId: z.string(),
      subscriptionId: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: z.object({ message: z.string() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Delete a scheduled plan change or cancellation",
  },
});
